import { addToursToProfile } from '@api/taptotip/profile';
import { fetchTourSelectionStatus } from '@api/taptotip/tour';
import { CircularProgress } from '@material-ui/core';
import { signOutOfTipDirect } from '@utils/signOut';
import React, { useEffect, useState } from 'react';
import { isExemptPath, markTourSelectionOpen, shouldCheckTourSelection } from './helpers';
import TourPicker from './TourPicker';

/**
 * First-time guide experience. Wraps every signed-in route (see
 * components/Routes) and, for a guide the backend says has no tours under a
 * company that has them, shows the tour picker in place of the page. It sits in
 * the route guard rather than behind its own URL so there is no page to
 * navigate around it.
 *
 * The answer is cached per user for the life of the tab, so moving between
 * pages does not refetch it. It fails open: if the status call errors, the
 * guide gets the page, not a dead end.
 */
let cache = { userId: null, promise: null, status: null };

const loadStatus = userId => {
  if (cache.userId !== userId) {
    cache = { userId, promise: null, status: null };
  }
  if (!cache.promise) {
    cache.promise = fetchTourSelectionStatus()
      .then(status => {
        if (cache.userId === userId) cache.status = status;
        return status;
      })
      .catch(() => {
        const status = { required: false, reason: 'error', tours: [] };
        if (cache.userId === userId) cache.status = status;
        return status;
      });
  }
  return cache.promise;
};

/**
 * The cached status for a guide, for the onboarding Tours step. Resolves to
 * `{ required: false }` straight away for anyone the gate would not check.
 */
export const useTourSelectionStatus = user => {
  const active = shouldCheckTourSelection(user);
  const cached = active && cache.userId === user.userId ? cache.status : null;
  const [status, setStatus] = useState(active ? cached : { required: false, tours: [] });

  useEffect(() => {
    if (!active || status) return undefined;
    let live = true;
    loadStatus(user.userId).then(result => {
      if (live) setStatus(result);
    });
    return () => {
      live = false;
    };
  }, [active, user && user.userId]);

  return status;
};

/** Save a guide's picks and stop the gate asking for them again this session. */
export const saveSelectedTours = async (userId, tourIds) => {
  await addToursToProfile(userId, tourIds);
  const previous = cache.userId === userId && cache.status ? cache.status : { tours: [] };
  const done = { ...previous, required: false, reason: 'has_tours' };
  cache = { userId, promise: Promise.resolve(done), status: done };
  return done;
};

export const resetTourSelectionCache = () => {
  cache = { userId: null, promise: null, status: null };
};

const TourSelectionGate = ({ user, path, children }) => {
  const active = shouldCheckTourSelection(user) && !isExemptPath(path);
  const cached = active && cache.userId === user.userId ? cache.status : null;
  const [status, setStatus] = useState(cached);

  useEffect(() => {
    if (!active || status) return undefined;
    let live = true;
    loadStatus(user.userId).then(result => {
      if (live) setStatus(result);
    });
    return () => {
      live = false;
    };
  }, [active, user && user.userId]);

  const showingPicker = active && !!status && status.required;
  useEffect(() => {
    if (!showingPicker) return undefined;
    markTourSelectionOpen(true);
    return () => markTourSelectionOpen(false);
  }, [showingPicker]);

  if (!active) return children;

  if (!status) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50" data-testid="tour-selection-loading">
        <CircularProgress />
      </div>
    );
  }

  if (!status.required) return children;

  const save = async tourIds => {
    setStatus(await saveSelectedTours(user.userId, tourIds));
  };

  return <TourPicker tours={status.tours || []} onSave={save} onSignOut={signOutOfTipDirect} />;
};

export default TourSelectionGate;
